/**
 * Whether App.tsx mounts the kids' top bar (components/dancer/DancerChrome.tsx).
 *
 * Its own file, apart from the bar's store in lib/dancerChromeFeed.ts, because
 * App.tsx imports this rule statically and every grown-up loads App.tsx's
 * chunk. With the rule kept here, the store, its device hint and the rest of the kids' bar
 * stay in the lazy chunks only a dancer opens (refute round on this branch:
 * importing the rule from the store put the whole store in the main chunk).
 *
 * No JSX and no runtime imports, so tests/dancer-menu-search.test.mjs runs it
 * straight under node.
 */

import type { JrStaffPromotionState } from './jrStaffPromotionState.ts';

/** What App.tsx knows about the signed-in dancer, as lib/dancer-session-context gives it. */
export interface DancerChromeSessionGate {
  authenticated: boolean;
  jrStaffDancer: boolean;
  jrStaffPromotion: JrStaffPromotionState;
}

/**
 * Whether App.tsx mounts the kids' bar: a dancer-only session (signed in on
 * the dancer cookie and not a promoted Jr Staff, App.tsx's own dancerOnly),
 * once her Jr Staff answer is in. A promoted Jr Staff keeps the grown-up
 * chrome, and while that answer is still 'pending' neither bar shows, so a
 * Jr Staff never sees the kids' bar flash up before the grown-up one.
 *
 * Here and not inline in App.tsx so the UI harness mounts the bar through the
 * very same rule (tests/harness/dancer-menu-search) and the unit test walks
 * every state of it (refute round on this branch: the harness mounted the bar
 * unconditionally, beside a stub in the one state the real App shows none).
 */
export function showsDancerChrome(session: DancerChromeSessionGate): boolean {
  return session.authenticated && !session.jrStaffDancer && session.jrStaffPromotion !== 'pending';
}
